// src/app/components/prompt-tracking/prompt-card.tsx

import { useState } from 'react';
import {
  ChevronDown, ChevronUp,
  TrendingUp, TrendingDown, Minus,
  MoreVertical, Edit, Trash2,
  Sparkles, Target, BarChart3, Users, MessageSquare,
  RefreshCw, CheckCircle2, AlertCircle, Loader2, PlayCircle,
  Bookmark,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import { InsightActionButton } from '../insight-action-button';
import { EditPromptModal }      from './edit-prompt-modal';
import { DeleteConfirmDialog }  from './delete-confirm-dialog';
import {
  getStatusStyle, getTrendConfig, getRecRateColor,
  getPositionStyle, getSentimentColor,
} from '../../constants/prompt-tracking-constants';
import { usePromptAnalysis }  from '../../hooks/prompt-tracking/usePromptAnalysis';
import { deletePrompt }       from '../../services/prompt-tracking/prompt-tracking-api';
import type { Prompt, TrendDirection } from '../../types/prompt-tracking-types';

const TREND_ICONS: Record<TrendDirection, React.ComponentType<any>> = {
  up: TrendingUp, down: TrendingDown, stable: Minus,
};

interface PromptCardProps {
  prompt:              Prompt;
  onAnalysisComplete?: (id: string) => void;
  onDeleted?:          (id: string) => void;
  onNavigate?:         (page: string) => void;
}

export function PromptCard({ prompt, onAnalysisComplete, onDeleted, onNavigate }: PromptCardProps) {
  const [isExpanded,       setIsExpanded]       = useState(false);
  const [expandedSection,  setExpandedSection]  = useState<string | null>(null);
  const [showActions,      setShowActions]      = useState(false);
  const [isHovered,        setIsHovered]        = useState(false);
  const [showEditModal,    setShowEditModal]    = useState(false);
  const [showDeleteConfirm,setShowDeleteConfirm]= useState(false);
  const [isDeleting,       setIsDeleting]       = useState(false);

  const {
    status: analysisStatus,
    progress,
    result: analysisResult,
    errorMessage,
    runAnalysis,
    reset: resetAnalysis,
  } = usePromptAnalysis();

  const brandName =
    localStorage.getItem('bm_brand') ||
    sessionStorage.getItem('bm_brand') ||
    'default';

  const trendCfg    = getTrendConfig(prompt.trend);
  const TrendIcon   = TREND_ICONS[prompt.trend];
  const statusStyle = getStatusStyle(prompt.status);

  const dp = analysisResult
    ? {
        ...prompt,
        recommendationRate: analysisResult.recommendationRate,
        shareOfVoice:       analysisResult.shareOfVoice,
        sentimentScore:     analysisResult.sentimentScore,
        averagePosition:    analysisResult.averagePosition,
        trend:              analysisResult.trend as TrendDirection,
        trendValue:         analysisResult.trendValue,
        aiInsight:          analysisResult.aiInsight,
        sentimentThemes:    analysisResult.sentimentThemes,
        modelBreakdown:     analysisResult.modelBreakdown,
        competitiveContext: analysisResult.competitiveContext,
      }
    : prompt;

  const handleRunAnalysis = (e: React.MouseEvent) => {
    e.stopPropagation();
    runAnalysis({
      promptId:   prompt.id,
      promptText: prompt.text,
      intent:     prompt.intent,
      brandName,
      models:     prompt.models,
    });
    setIsExpanded(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await deletePrompt(prompt.id);
      onDeleted?.(prompt.id);
    } catch { /* keep dialog open */ }
    finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <>
      <div
        className="border-b transition-colors"
        style={{ borderColor: 'var(--border)' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => { setIsHovered(false); setShowActions(false); }}
      >
        {/* ── Collapsed row — 2-row stacked layout on mobile ──────────── */}
        <div
          className="py-3 px-3 md:px-4 cursor-pointer hover:bg-muted/30 transition-colors"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {/* Row 1: trend icon + prompt text */}
          <div className="flex items-center gap-3 min-w-0 mb-2 md:mb-0 md:inline-flex md:w-[calc(100%-0px)]">
            <div className="flex md:hidden w-full items-center gap-3 min-w-0">
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <TrendIcon className="w-4 h-4" style={{ color: trendCfg.color }} />
                <span
                  style={{
                    fontSize: 'var(--text-xs)',
                    color: trendCfg.color,
                    fontWeight: 'var(--font-weight-medium)',
                  }}
                >
                  {dp.trendValue}
                </span>
              </div>
              <span
                className="min-w-0 flex-1"
                style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--foreground)',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {prompt.text}
              </span>
            </div>
          </div>

          {/* Desktop (md+): single-row original layout.
              Mobile (<md): rendered separately below as stacked content. */}
          <div className="hidden md:flex items-center justify-between gap-3">
            {/* Left: trend + text */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <TrendIcon className="w-4 h-4" style={{ color: trendCfg.color }} />
                <span
                  style={{
                    fontSize: 'var(--text-xs)',
                    color: trendCfg.color,
                    fontWeight: 'var(--font-weight-medium)',
                  }}
                >
                  {dp.trendValue}
                </span>
              </div>
              <span
                className="truncate"
                style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--foreground)',
                }}
              >
                {prompt.text}
              </span>
            </div>

            {/* Right: metrics + controls */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <span
                style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: getRecRateColor(dp.recommendationRate),
                  minWidth: '45px',
                  textAlign: 'right',
                }}
              >
                {dp.recommendationRate > 0 ? `${dp.recommendationRate}%` : '—'}
              </span>

              <div
                className="flex items-center gap-1 px-2 py-0.5 rounded-md"
                style={{ backgroundColor: 'var(--muted)', flexShrink: 0 }}
              >
                <PlayCircle className="w-3 h-3" style={{ color: 'var(--muted-foreground)' }} />
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>
                  {prompt.executionCount}x
                </span>
              </div>

              <span
                style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--muted-foreground)',
                  minWidth: '70px',
                }}
              >
                {prompt.lastRun}
              </span>

              <span
                className="px-2 py-0.5 rounded-md"
                style={{
                  ...statusStyle,
                  fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--font-weight-medium)',
                  minWidth: '80px',
                  textAlign: 'center',
                }}
              >
                {statusStyle.label}
              </span>

              <RunAnalysisButton
                status={analysisStatus}
                progress={progress}
                onClick={handleRunAnalysis}
                onReset={(e) => { e.stopPropagation(); resetAnalysis(); }}
              />

              <div className="relative flex-shrink-0" style={{ width: '24px' }}>
                {isHovered && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowActions(!showActions); }}
                    className="p-1 rounded transition-colors"
                    style={{
                      backgroundColor: showActions ? 'var(--muted)' : 'transparent',
                      color: 'var(--foreground)',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>
                )}
                {showActions && (
                  <ActionsMenu
                    onEdit={() => { setShowEditModal(true); setShowActions(false); }}
                    onRunAgain={(e) => { e.stopPropagation(); setShowActions(false); handleRunAnalysis(e); }}
                    onDelete={() => { setShowDeleteConfirm(true); setShowActions(false); }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Mobile (<md): Row 2 — metrics/controls wrap freely */}
          <div className="flex md:hidden items-center gap-2 flex-wrap">
            <span
              style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                color: getRecRateColor(dp.recommendationRate),
              }}
            >
              {dp.recommendationRate > 0 ? `${dp.recommendationRate}%` : '—'}
            </span>

            <div
              className="flex items-center gap-1 px-2 py-0.5 rounded-md"
              style={{ backgroundColor: 'var(--muted)' }}
            >
              <PlayCircle className="w-3 h-3" style={{ color: 'var(--muted-foreground)' }} />
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>
                {prompt.executionCount}x
              </span>
            </div>

            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>
              {prompt.lastRun}
            </span>

            <span
              className="px-2 py-0.5 rounded-md"
              style={{
                ...statusStyle,
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-weight-medium)',
                textAlign: 'center',
              }}
            >
              {statusStyle.label}
            </span>

            <div className="ml-auto flex items-center gap-2">
              <RunAnalysisButton
                status={analysisStatus}
                progress={progress}
                onClick={handleRunAnalysis}
                onReset={(e) => { e.stopPropagation(); resetAnalysis(); }}
              />

              {/* On mobile, always show the menu button (no hover available) */}
              <div className="relative flex-shrink-0">
                <button
                  onClick={(e) => { e.stopPropagation(); setShowActions(!showActions); }}
                  className="p-1 rounded transition-colors"
                  style={{
                    backgroundColor: showActions ? 'var(--muted)' : 'transparent',
                    color: 'var(--foreground)',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
                {showActions && (
                  <ActionsMenu
                    onEdit={() => { setShowEditModal(true); setShowActions(false); }}
                    onRunAgain={(e) => { e.stopPropagation(); setShowActions(false); handleRunAnalysis(e); }}
                    onDelete={() => { setShowDeleteConfirm(true); setShowActions(false); }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Expanded detail ─────────────────────────────────────────── */}
        {isExpanded && (
          <ExpandedDetail
            prompt={dp}
            brandName={brandName}
            analysisStatus={analysisStatus}
            progress={progress}
            errorMessage={errorMessage}
            expandedSection={expandedSection}
            onToggleSection={(s) => setExpandedSection((p) => (p === s ? null : s))}
            onNavigate={onNavigate}
          />
        )}
      </div>

      {showEditModal && (
        <EditPromptModal
          prompt={prompt}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onUpdated={() => { onAnalysisComplete?.(prompt.id); setShowEditModal(false); }}
          brandName={brandName}
        />
      )}

      {showDeleteConfirm && (
        <DeleteConfirmDialog
          promptText={prompt.text}
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Run Analysis button
// ─────────────────────────────────────────────────────────────────────────────

function RunAnalysisButton({ status, progress, onClick, onReset }: {
  status: string;
  progress: { done: number; total: number; percent: number } | null;
  onClick:  (e: React.MouseEvent) => void;
  onReset:  (e: React.MouseEvent) => void;
}) {
  if (status === 'running' || status === 'connecting') {
    return (
      <div
        className="flex items-center gap-1.5 px-2 py-1 rounded-lg whitespace-nowrap"
        style={{
          backgroundColor: 'var(--signal-blue-10)',
          fontSize: 'var(--text-xs)',
          color: 'var(--signal-blue)',
        }}
      >
        <Loader2 className="w-3 h-3 animate-spin" />
        {progress ? `${progress.percent.toFixed(0)}%` : 'Starting…'}
      </div>
    );
  }
  if (status === 'done') {
    return (
      <button
        onClick={onReset}
        className="flex items-center gap-1.5 px-2 py-1 rounded-lg whitespace-nowrap"
        style={{
          backgroundColor: 'var(--success-bg)',
          fontSize: 'var(--text-xs)',
          color: 'var(--success-text)',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        <CheckCircle2 className="w-3 h-3" /> Done
      </button>
    );
  }
  if (status === 'error') {
    return (
      <button
        onClick={onReset}
        className="flex items-center gap-1.5 px-2 py-1 rounded-lg whitespace-nowrap"
        style={{
          backgroundColor: 'var(--infrared-coral-10)',
          fontSize: 'var(--text-xs)',
          color: 'var(--infrared-coral)',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        <AlertCircle className="w-3 h-3" /> Retry
      </button>
    );
  }
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 px-2 py-1 rounded-lg transition-all whitespace-nowrap"
      style={{
        backgroundColor: 'var(--signal-blue-10)',
        fontSize: 'var(--text-xs)',
        color: 'var(--signal-blue)',
        border: '1px solid var(--signal-blue)',
        cursor: 'pointer',
        fontWeight: 'var(--font-weight-medium)',
      }}
    >
      <RefreshCw className="w-3 h-3" /> Run Analysis
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Actions menu
// ─────────────────────────────────────────────────────────────────────────────

function ActionsMenu({ onEdit, onRunAgain, onDelete }: {
  onEdit:     () => void;
  onRunAgain: (e: React.MouseEvent) => void;
  onDelete:   () => void;
}) {
  const base: React.CSSProperties = {
    fontSize: 'var(--text-sm)',
    color: 'var(--foreground)',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
  };
  return (
    <div
      className="absolute right-0 top-full mt-2 rounded-lg shadow-lg border py-1 z-10"
      style={{
        backgroundColor: 'var(--background)',
        borderColor: 'var(--border)',
        minWidth: '180px',
      }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        className="w-full px-3 py-2 flex items-center gap-2 hover:bg-muted transition-colors"
        style={base}
      >
        <Edit className="w-4 h-4" /> Edit Prompt
      </button>
      <button
        onClick={onRunAgain}
        className="w-full px-3 py-2 flex items-center gap-2 hover:bg-muted transition-colors"
        style={base}
      >
        <RefreshCw className="w-4 h-4" /> Run Analysis Again
      </button>
      <div style={{ height: '1px', backgroundColor: 'var(--border)', margin: '4px 0' }} />
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="w-full px-3 py-2 flex items-center gap-2 hover:bg-muted transition-colors"
        style={{ ...base, color: 'var(--infrared-coral)' }}
      >
        <Trash2 className="w-4 h-4" /> Delete Prompt
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Expanded detail panel
// ─────────────────────────────────────────────────────────────────────────────

function ExpandedDetail({
  prompt, brandName, analysisStatus, progress, errorMessage,
  expandedSection, onToggleSection, onNavigate,
}: {
  prompt:           Prompt;
  brandName:        string;
  analysisStatus:   string;
  progress:         { done: number; total: number; percent: number } | null;
  errorMessage:     string | null;
  expandedSection:  string | null;
  onToggleSection:  (s: string) => void;
  onNavigate?:      (page: string) => void;
}) {
  const trendCfg  = getTrendConfig(prompt.trend);
  const TrendIcon = TREND_ICONS[prompt.trend];

  const insightContent = [
    `Prompt: "${prompt.text}"`,
    `Intent: ${prompt.intent}`,
    prompt.recommendationRate > 0 ? `Recommendation Rate: ${prompt.recommendationRate}%` : null,
    prompt.shareOfVoice       > 0 ? `Share of Voice: ${prompt.shareOfVoice}%` : null,
    prompt.sentimentScore     > 0 ? `Sentiment: ${prompt.sentimentScore}%` : null,
    prompt.averagePosition    > 0 ? `Avg Position: #${prompt.averagePosition.toFixed(1)}` : null,
    prompt.aiInsight ? `AI Insight: ${prompt.aiInsight}` : null,
  ].filter(Boolean) as string[];

  const hasMetrics = prompt.recommendationRate > 0;

  return (
    <div className="border-t" style={{ borderColor: 'var(--border)' }}>

      {/* Progress bar */}
      {(analysisStatus === 'connecting' || analysisStatus === 'running') && (
        <div className="px-3 md:px-4 pt-4 pb-2">
          <div className="flex items-center justify-between mb-2 gap-2">
            <span
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--muted-foreground)',
                overflowWrap: 'anywhere',
              }}
            >
              {analysisStatus === 'connecting'
                ? 'Connecting to AI models…'
                : `Analyzing across ${progress?.total ?? '?'} models…`}
            </span>
            <span
              style={{
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--signal-blue)',
                flexShrink: 0,
              }}
            >
              {progress ? `${progress.done}/${progress.total}` : ''}
            </span>
          </div>
          <div
            className="w-full h-1.5 rounded-full overflow-hidden"
            style={{ backgroundColor: 'var(--muted)' }}
          >
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${progress?.percent ?? 0}%`, backgroundColor: 'var(--signal-blue)' }}
            />
          </div>
          {progress && (
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {Array.from({ length: progress.total }).map((_, i) => (
                <span key={i}>
                  {i < progress.done
                    ? <CheckCircle2 className="w-3.5 h-3.5" style={{ color: 'var(--success-text)' }} />
                    : i === progress.done
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: 'var(--signal-blue)' }} />
                    : <div className="w-3.5 h-3.5 rounded-full border" style={{ borderColor: 'var(--border)' }} />}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {analysisStatus === 'error' && errorMessage && (
        <div
          className="mx-3 md:mx-4 mt-3 px-3 py-2 rounded-lg flex items-start gap-2"
          style={{
            backgroundColor: 'var(--infrared-coral-10)',
            color: 'var(--infrared-coral)',
            fontSize: 'var(--text-sm)',
          }}
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span
            style={{
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            {errorMessage}
          </span>
        </div>
      )}

      {/* Metrics grid — 2 cols mobile, 3 cols sm, 5 cols md+ */}
      {hasMetrics && (
        <div className="px-3 md:px-4 py-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4 mb-4">
            <MetricCell label="Rec Rate" value={`${prompt.recommendationRate}%`}>
              <div className="flex items-center gap-0.5">
                <TrendIcon className="w-3 h-3" style={{ color: trendCfg.color }} />
                <span style={{ fontSize: 'var(--text-xs)', color: trendCfg.color }}>
                  {prompt.trendValue}
                </span>
              </div>
            </MetricCell>
            <MetricCell label="Share of Voice" value={`${prompt.shareOfVoice}%`} />
            <MetricCell label="Sentiment"      value={`${prompt.sentimentScore}%`} />
            <MetricCell label="Avg Position"   value={`#${prompt.averagePosition.toFixed(1)}`} />
            <MetricCell label="Analysis Runs"  value={String(prompt.executionCount)} />
          </div>

          {/* AI insight + inline save button — stacks on mobile */}
          {prompt.aiInsight && (
            <div
              className="rounded-lg p-3"
              style={{
                backgroundColor: 'var(--spectral-violet-4)',
                borderLeft: '3px solid var(--spectral-violet)',
              }}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-start gap-2 flex-1 min-w-0">
                  <Sparkles
                    className="w-4 h-4 flex-shrink-0 mt-0.5"
                    style={{ color: 'var(--spectral-violet)' }}
                  />
                  <p
                    style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--foreground)',
                      lineHeight: '1.4',
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',
                    }}
                  >
                    {prompt.aiInsight}
                  </p>
                </div>
                <div className="flex-shrink-0 self-start sm:self-auto">
                  <InsightActionButton
                    title={`Prompt: "${prompt.text}"`}
                    content={insightContent}
                    context={{
                      module: 'prompt-tracking',
                      stageName: 'Prompt Tracking',
                      timestamp: new Date(),
                    }}
                    brandName={brandName}
                    size="sm"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* No data yet */}
      {!hasMetrics && analysisStatus === 'idle' && (
        <div className="px-3 md:px-4 py-6 text-center">
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>
            No analysis data yet. Click <strong>Run Analysis</strong> to get insights.
          </p>
        </div>
      )}

      {/* Sub-sections */}
      {hasMetrics && (
        <>
          <SentimentSection   prompt={prompt} isOpen={expandedSection === 'sentiment'}   onToggle={() => onToggleSection('sentiment')} />
          <ModelBreakdown     prompt={prompt} isOpen={expandedSection === 'models'}      onToggle={() => onToggleSection('models')} />
          <CompetitiveSection prompt={prompt} isOpen={expandedSection === 'competitive'} onToggle={() => onToggleSection('competitive')} />
          <HistoricalSection  prompt={prompt} isOpen={expandedSection === 'historical'}  onToggle={() => onToggleSection('historical')} />
        </>
      )}

      {/* Save to Insights block — stacks on mobile */}
      {hasMetrics && (
        <div
          className="mx-3 md:mx-4 mb-4 mt-2 p-3 md:p-4 rounded-xl border-l-4"
          style={{
            backgroundColor: 'var(--muted)',
            borderLeftColor: 'var(--spectral-violet)',
          }}
        >
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <Bookmark className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--spectral-violet)' }} />
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--foreground)',
                  }}
                >
                  Save this analysis to Insights
                </span>
              </div>
              <p
                style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--muted-foreground)',
                  lineHeight: '1.5',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                Saves prompt text, recommendation rate ({prompt.recommendationRate}%), share of voice ({prompt.shareOfVoice}%),
                sentiment ({prompt.sentimentScore}%)
                {prompt.aiInsight ? ', and the AI insight' : ''} to your Insights library for report generation.
              </p>
            </div>
            <div className="flex-shrink-0 self-start sm:self-auto">
              <InsightActionButton
                title={`Prompt Intelligence: "${prompt.text.slice(0, 60)}${prompt.text.length > 60 ? '…' : ''}"`}
                content={insightContent}
                context={{
                  module:    'prompt-tracking',
                  stageName: 'Prompt Intelligence',
                  timestamp: new Date(),
                }}
                brandName={brandName}
                onNavigateToInsights={() => onNavigate?.('report-generator')}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function MetricCell({ label, value, children }: { label: string; value: string; children?: React.ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontSize: 'var(--text-xs)',
          color: 'var(--muted-foreground)',
          marginBottom: '4px',
        }}
      >
        {label}
      </div>
      <div className="flex items-baseline gap-1 flex-wrap">
        <span
          style={{
            fontSize: 'var(--text-xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--foreground)',
          }}
        >
          {value}
        </span>
        {children}
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, iconColor, title, subtitle, isOpen, onToggle }: any) {
  return (
    <div className="border-t" style={{ borderColor: 'var(--border)' }}>
      <button
        onClick={onToggle}
        className="w-full px-3 md:px-4 py-3 flex items-center justify-between gap-3 hover:bg-muted/30 transition-colors"
        style={{ border: 'none', backgroundColor: 'transparent', cursor: 'pointer', textAlign: 'left' }}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1 flex-wrap">
          <Icon className="w-4 h-4 flex-shrink-0" style={{ color: iconColor }} />
          <h4
            style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--foreground)',
            }}
          >
            {title}
          </h4>
          {subtitle && (
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>
              {subtitle}
            </span>
          )}
        </div>
        {isOpen
          ? <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--muted-foreground)' }} />
          : <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--muted-foreground)' }} />}
      </button>
    </div>
  );
}

function SentimentSection({ prompt, isOpen, onToggle }: { prompt: Prompt; isOpen: boolean; onToggle: () => void }) {
  return (
    <>
      <SectionHeader
        icon={MessageSquare}
        iconColor="var(--spectral-violet)"
        title="Sentiment Analysis"
        isOpen={isOpen}
        onToggle={onToggle}
      />
      {isOpen && (
        <div
          className="px-3 md:px-4 pb-4"
          style={{ borderLeft: '3px solid var(--spectral-violet)', marginLeft: '12px' }}
        >
          <div className="space-y-4 pl-3 md:pl-4">
            <div>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--foreground)' }}>
                  {prompt.sentimentScore}% Positive
                </span>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>
                  {100 - prompt.sentimentScore}% Negative
                </span>
              </div>
              <div
                className="w-full h-2 rounded-full overflow-hidden"
                style={{ backgroundColor: 'var(--muted)' }}
              >
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${prompt.sentimentScore}%`,
                    backgroundColor: 'var(--success-text)',
                  }}
                />
              </div>
            </div>
            {[
              ['Positive Themes', prompt.sentimentThemes.positive, 'var(--success-text)'],
              ['Negative Themes', prompt.sentimentThemes.negative, 'var(--infrared-coral)'],
            ].map(([t, themes, c]: any) => (
              <div key={t}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-1 h-4 rounded-full" style={{ backgroundColor: c }} />
                  <h5
                    style={{
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--foreground)',
                    }}
                  >
                    {t}
                  </h5>
                </div>
                <ul className="space-y-1 ml-3">
                  {themes.map((th: string, i: number) => (
                    <li
                      key={i}
                      style={{
                        fontSize: 'var(--text-sm)',
                        color: 'var(--muted-foreground)',
                        lineHeight: '1.5',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                      }}
                    >
                      • {th}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function ModelBreakdown({ prompt, isOpen, onToggle }: { prompt: Prompt; isOpen: boolean; onToggle: () => void }) {
  return (
    <>
      <SectionHeader
        icon={BarChart3}
        iconColor="var(--signal-blue)"
        title="Model Breakdown"
        subtitle={`${prompt.models.length} models`}
        isOpen={isOpen}
        onToggle={onToggle}
      />
      {isOpen && (
        <div
          className="px-3 md:px-4 pb-4"
          style={{ borderLeft: '3px solid var(--signal-blue)', marginLeft: '12px' }}
        >
          <div className="space-y-3 pl-3 md:pl-4">
            {prompt.modelBreakdown.map((m) => (
              <div key={m.modelId} className="py-2">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div
                    style={{
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--foreground)',
                    }}
                  >
                    {m.modelName}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="px-2 py-0.5 rounded-md"
                      style={{
                        ...getPositionStyle(m.position),
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-weight-semibold)',
                      }}
                    >
                      #{m.position}
                    </span>
                    <div className="flex items-center gap-1">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: getSentimentColor(m.sentiment) }}
                      />
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>
                        {m.sentiment}% sentiment
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between mb-1">
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>
                    Recommendation Rate
                  </span>
                  <span
                    style={{
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-bold)',
                      color: getRecRateColor(m.recommendationRate),
                    }}
                  >
                    {m.recommendationRate}%
                  </span>
                </div>
                <div
                  className="w-full h-2 rounded-full overflow-hidden"
                  style={{ backgroundColor: 'var(--muted)' }}
                >
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${m.recommendationRate}%`,
                      backgroundColor: getRecRateColor(m.recommendationRate),
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function CompetitiveSection({ prompt, isOpen, onToggle }: { prompt: Prompt; isOpen: boolean; onToggle: () => void }) {
  const cc = prompt.competitiveContext;
  return (
    <>
      <SectionHeader
        icon={Users}
        iconColor="var(--alpha-teal)"
        title="Competitive Context"
        subtitle={`#${cc.yourRank} of ${cc.totalCompetitors}`}
        isOpen={isOpen}
        onToggle={onToggle}
      />
      {isOpen && (
        <div
          className="px-3 md:px-4 pb-4"
          style={{ borderLeft: '3px solid var(--alpha-teal)', marginLeft: '12px' }}
        >
          <div className="pl-3 md:pl-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {cc.topCompetitors.map((c, i) => (
              <div
                key={i}
                className="p-3 rounded-lg"
                style={{ backgroundColor: 'var(--muted-30)' }}
              >
                <div
                  style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--muted-foreground)',
                    marginBottom: '4px',
                  }}
                >
                  #{i + 1}
                </div>
                <div
                  style={{
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--foreground)',
                    marginBottom: '4px',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  }}
                >
                  {c.name}
                </div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>
                  {c.shareOfVoice}% share
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function HistoricalSection({ prompt, isOpen, onToggle }: { prompt: Prompt; isOpen: boolean; onToggle: () => void }) {
  return (
    <>
      <SectionHeader
        icon={Target}
        iconColor="var(--chart-1)"
        title="Historical Trends"
        subtitle={`${prompt.executionCount} runs`}
        isOpen={isOpen}
        onToggle={onToggle}
      />
      {isOpen && (
        <div
          className="px-3 md:px-4 pb-4"
          style={{ borderLeft: '3px solid var(--chart-1)', marginLeft: '12px' }}
        >
          <div className="pl-3 md:pl-4">
            <div style={{ width: '100%', height: 'clamp(200px, 35vw, 260px)' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={prompt.historicalData}
                  margin={{ top: 10, right: 10, bottom: 10, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }}
                    stroke="var(--muted-foreground)"
                  />
                  <YAxis
                    tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }}
                    stroke="var(--muted-foreground)"
                    width={36}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--background)',
                      borderColor: 'var(--border)',
                      fontSize: '12px',
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="recommendationRate"
                    name="Rec Rate"
                    stroke="var(--signal-blue)"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="sentiment"
                    name="Sentiment"
                    stroke="var(--alpha-teal)"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </>
  );
}